/**
 * Sprint 127 — Gramos y mililitros en la captura; kilos y litros en la conversación.
 *
 * El reporte que lo originó, del dueño, tras una retro con la dueña:
 *
 * > *"me dijo que KILOS sólo lo vende en 1 kg, 3/4, 1/2 y 1/4… y con qué me sale hoy:
 * > **para agregar 125 gramos de costilla**. Y apuesto a que en unos días dirá: ¿cómo
 * > vendo paquetes de 1 kilo y medio, o de 1 kilo 250 gramos?"*
 *
 * Tenía razón en la apuesta, y el sistema tenía la culpa: `0.125` se rechazaba con un
 * *"no es una cantidad que este ingrediente permita"* porque lo que se pesa nacía con una
 * rejilla de cuartos de kilo. Nadie la pidió — se coló en el Sprint 109 al abrir "medio
 * pollo", y contradecía la regla del Sprint 61, donde lo que se pesa admitía cualquier
 * cantidad.
 *
 * ## Por qué se captura en gramos y no en kilos
 *
 * Porque `0.125` es un número que hay que traducir y `125` es un número que se lee. En el
 * piso, un dedazo de coma —`1.25` donde iba `0.125`— descuenta diez veces de menos en cada
 * venta y no hay pantalla donde se note. En gramos ese error no existe: no hay coma que
 * poner mal.
 *
 * Lo que **no** se cambia aquí es cómo se habla: nadie dice "quedan 3750 gramos de
 * costilla". El almacén y las pantallas siguen leyéndose en kilos y litros; los gramos son
 * la unidad en la que se **teclea**. Es el mismo trato que ya reciben los centavos.
 *
 * > La base sigue guardando kilos y litros (`Float`). Convertir el almacenamiento a enteros
 * > es un sprint aparte, con respaldo del tenant: toca stock, recetas, paquetes, movimientos
 * > e histórico de un cliente que ya opera.
 */

/** Unidades que admiten cualquier cantidad porque se pesan o se miden. */
const CONTINUAS = new Set(['kg', 'litro']);

/**
 * ¿Esta unidad es continua (se pesa / se mide) o discreta (se cuenta)?
 *
 * Es la única pregunta de la que dependen la rejilla, la etiqueta y la captura. Estaba
 * repetida en cinco archivos con tres redacciones distintas, y ahí es donde se desfasaron.
 */
export function esUnidadContinua(unidad: string): boolean {
  return CONTINUAS.has(unidad.trim().toLowerCase());
}

export interface UnidadDeCaptura {
  /** Cómo se rotula el campo: "gramos", "ml", "piezas". */
  nombre: string;
  /** Símbolo corto para las etiquetas: "g", "ml", "". */
  simbolo: string;
  /** Cuántas unidades de captura entran en una unidad base. 1000 g = 1 kg. */
  factor: number;
}

/**
 * En qué unidad se teclea una cantidad de esta unidad base.
 *
 * Lo que se cuenta se teclea tal cual: medio pollo son `0.5` piezas y ningún operador va a
 * escribir "500 mili-pollos".
 */
export function unidadDeCaptura(unidad: string): UnidadDeCaptura {
  const u = unidad.trim().toLowerCase();
  if (u === 'kg') return { nombre: 'gramos', simbolo: 'g', factor: 1000 };
  if (u === 'litro') return { nombre: 'ml', simbolo: 'ml', factor: 1000 };
  return { nombre: u === 'pieza' ? 'piezas' : u, simbolo: '', factor: 1 };
}

/** De lo que se tecleó (125 g) a lo que se guarda (0.125 kg). */
export function aCantidadBase(valorCapturado: number, unidad: string): number {
  const { factor } = unidadDeCaptura(unidad);
  if (!Number.isFinite(valorCapturado)) return 0;
  if (factor === 1) return valorCapturado;
  return limpiar(valorCapturado / factor);
}

/**
 * De lo guardado (0.125 kg) a lo que se teclea (125 g).
 *
 * Redondea al gramo entero a propósito: es la precisión con la que se compra carne, y un
 * `0.3333` heredado que se vuelve `333` al abrir la pantalla pierde 0.3 gramos, no una venta.
 */
export function aCantidadCapturada(cantidadBase: number, unidad: string): number {
  const { factor } = unidadDeCaptura(unidad);
  if (!Number.isFinite(cantidadBase)) return 0;
  if (factor === 1) return limpiar(cantidadBase);
  return Math.round(cantidadBase * factor);
}

/**
 * Una cantidad dicha como la diría alguien en la cocina.
 *
 * Menos de una unidad se dice en gramos ("125 g"); de ahí para arriba, en kilos ("1.5 kg").
 * Es la frontera que usa cualquier carnicería.
 */
export function decirCantidad(cantidadBase: number, unidad: string): string {
  const u = unidad.trim().toLowerCase();
  if (!esUnidadContinua(u)) return `${limpiar(cantidadBase)} ${u}`;
  const { simbolo, factor } = unidadDeCaptura(u);
  if (cantidadBase > 0 && cantidadBase < 1) {
    return `${Math.round(cantidadBase * factor)} ${simbolo}`;
  }
  return `${limpiar(cantidadBase)} ${u}`;
}

/**
 * A cuánto sale la unidad base de un formato: el *"sale a $560 el kilo"*.
 *
 * Existe para que el precio de cada tamaño se pueda comparar con el de al lado. En el
 * catálogo real del negocio el kilo de sirloin va a $920 y el medio kilo a $505 —el precio
 * NO es una regla de tres y no se calcula nunca—, pero un 1/8 tecleado a $70 sale al doble
 * del kilo, y eso sí es un dedazo que hoy no ve nadie.
 *
 * Devuelve centavos por unidad base, o `null` si no hay con qué calcularlo.
 */
export function precioPorUnidadBase(
  precioCentavos: number,
  cantidadBase: number,
): number | null {
  if (!(precioCentavos > 0) || !(cantidadBase > 0)) return null;
  return Math.round(precioCentavos / cantidadBase);
}

/** Quita la basura de coma flotante sin alterar el valor real. */
function limpiar(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}
